<script setup lang="ts">
import KpTextfield from '@/components/kp-textfield.vue'
import { useToast } from '@/composables/toast'
import { errToStr } from '@/helpers/tools'
import { computed } from 'vue'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { instance as api } from '@/helpers/api'
import { formRules } from '@/helpers/validators'
import KpToolbar from '@/components/kp-toolbar.vue'

const loading = ref(false)
const oldPw = ref('')
const newPw = ref('')
const newPwConfirm = ref('')

const toast = useToast()
const { t } = useI18n()
const router = useRouter()

const passwordsValid = computed(() => {
    return newPw.value.length > 0 && newPw.value === newPwConfirm.value
})
async function onChangePW() {
    loading.value = true
    try {
        await api().post('/auth/changePassword', {
            old_password: oldPw.value,
            new_password: newPw.value,
        })
        toast.add({ text: t('pw_change_successful'), color: 'success' })
        router.push({ name: 'home' })
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <div>
        <KpToolbar :title="$t('change_my_password')" icon="fas fa-key" />
        <v-card max-width="600px" class="centered-container">
            <v-form>
                <v-alert
                    type="info"
                    :icon="false"
                    border="start"
                    colored-border
                    elevation="2"
                    variant="text"
                >
                    <span>{{ t('new_password_requires') }}</span>
                    <ul>
                        <li>{{ t('at_least_eight_characters') }}</li>
                        <li>{{ t('at_least_one_letter') }}</li>
                        <li>{{ t('at_least_one_digit') }}</li>
                        <li>{{ t('diffrent_to_last_password') }}</li>
                    </ul>
                    <div>{{ t('for_example') + ": 'abcd1234'" }}</div>
                </v-alert>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <kp-textfield
                                    v-model:model-value="oldPw"
                                    icon-type="password"
                                    type="password"
                                    :label="t('old_password')"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <kp-textfield
                                    v-model:model-value="newPw"
                                    icon-type="password"
                                    :label="t('new_password')"
                                    :rules="[formRules.minLength(8)]"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <kp-textfield
                                    v-model:model-value="newPwConfirm"
                                    icon-type="password"
                                    :label="t('new_password_confirm')"
                                    :error-messages="passwordsValid ? null : t('pw_not_match')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="action white--text"
                        variant="flat"
                        :disabled="oldPw.length < 1 || !passwordsValid || loading"
                        @click="onChangePW"
                        prepend-icon="fa-solid fa-key"
                    >
                        {{ t('change_my_password') }}
                    </v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </div>
</template>

<style lang="css" scoped>
.centered-container {
    margin-left: auto;
    margin-right: auto;
    margin-top: 1rem;
    padding: 1rem 1rem 1rem 2rem;
}
</style>
